import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '../ui/states';

/**
 * Nạp một báo cáo khi cuộn **tới gần** nó — APM-REPORT-lazy.
 *
 * ## Vấn đề
 *
 * Một nhóm báo cáo có tới cả chục biểu đồ, và mỗi biểu đồ tự gọi `useQuery`
 * ngay khi mount. Mở tab Báo cáo là bắn cả chục lượt gọi cùng lúc rồi dựng cả
 * chục `ResponsiveContainer` của recharts trong một khung hình — trang khựng
 * đúng lúc người dùng vừa bấm vào.
 *
 * Phần lớn số ấy còn nằm **dưới màn hình**: người đọc chưa nhìn thấy chúng, và
 * có thể sẽ không cuộn xuống tới nơi.
 *
 * ## Cách làm
 *
 * Giữ chỗ bằng một khung trống cùng kích thước, và chỉ dựng biểu đồ thật khi nó
 * vào **gần** tầm nhìn. "Gần" chứ không phải "đã thấy": `LOOKAHEAD` cho nó chạy
 * trước một màn hình, nên phần lớn lần cuộn tới nơi thì dữ liệu đã về.
 *
 * ## Ba chi tiết, và bỏ cái nào cũng hỏng theo kiểu riêng
 *
 * 1. **Dựng rồi thì ở lại.** Gỡ ra khi cuộn qua là mỗi lần cuộn lên cuộn xuống
 *    một lượt gọi mạng, và chiều cao đổi liên tục làm thanh cuộn nhảy.
 * 2. **Khung giữ chỗ phải cao xấp xỉ biểu đồ thật.** Cao 0 thì cả mười báo cáo
 *    chồng lên nhau trong một màn hình, `IntersectionObserver` thấy **tất cả**
 *    cùng lúc, và ta quay lại đúng chỗ xuất phát.
 * 3. **Không có `IntersectionObserver` thì hiện ngay.** jsdom không có nó, và
 *    một trình duyệt cũ cũng vậy — thà tải hết còn hơn một trang trắng vĩnh
 *    viễn.
 */

/** Chạy trước tầm nhìn một quãng, để cuộn tới nơi là đã có số. */
const LOOKAHEAD = '600px';

/**
 * Cao xấp xỉ một `ChartFrame` đang ở trạng thái tải: viền + `p-3` + tiêu đề +
 * `Skeleton h-56`. Không cần khớp từng pixel — chỉ cần đủ để mười báo cáo không
 * cùng lọt vào một màn hình.
 */
const PLACEHOLDER_MIN_HEIGHT = '17rem';

export function LazyReport({ children }: { readonly children: ReactNode }): ReactNode {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(() => typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    if (shown) return;
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        setShown(true);
        // Ngắt ngay: báo cáo đã dựng thì ở lại, không có gì để theo dõi nữa.
        observer.disconnect();
      },
      { rootMargin: LOOKAHEAD },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [shown]);

  return (
    <div ref={ref}>
      {shown ? (
        children
      ) : (
        <section
          className="flex flex-col gap-2 rounded-DEFAULT border border-border p-3"
          style={{ minHeight: PLACEHOLDER_MIN_HEIGHT }}
          aria-busy
          aria-label={t('reports.loadingOnScroll')}
        >
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-56 w-full flex-1" />
        </section>
      )}
    </div>
  );
}
